import { Component, Input, OnInit, inject } from '@angular/core';
import {
  StudyMaterial,
  StudyMaterialResponseDTO,
} from '../../shared/models/study-material.model';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import { FormControl } from '@angular/forms';
import { ToastService } from '@black/ui';
import { HttpClient } from '@angular/common/http';
import { getApiHostUrl } from '../../shared/util/const';
import { catchError, EMPTY } from 'rxjs';
import { LiveClass } from '../../shared/core/state/live-class/models/live-class';
import { SubscriptionStatus } from '../../shared/core/state/subscription/subscription.model';
import { SessionSelection } from '../../shared/component/session-picker/session-picker.component';

@Component({
  selector: 'black-study-materials',
  standalone: false,

  templateUrl: './study-materials.component.html',
  styleUrl: './study-materials.component.css',
})
export class StudyMaterialsComponent implements OnInit {
  private liveClassService = inject(LiveClassService);
  private toastService = inject(ToastService);
  private http = inject(HttpClient);


  ngOnInit(): void {
    this.fetchAllStudyMaterials();
  }

  @Input()
  editModeEnabled = false;

  materialNameControl = new FormControl('', { nonNullable: true });

  @Input()
  selectedLiveClassId: string | null = null;

  @Input()
  selectedLiveClass: LiveClass | null = null;

  studyMaterials: StudyMaterial[] = [];

  selectedSession: SessionSelection | null = null;

  onSessionSelected(selection: SessionSelection): void {
    this.selectedSession = selection;
  }

  addStudyMaterial(cb: () => void) {
    if (this.materialNameControl.valid && this.selectedLiveClassId) {
      const materialName = this.materialNameControl.value;
      const meetingId = this.selectedSession?.meetingId ?? undefined;
      const sessionDate = this.selectedSession?.sessionDate ?? undefined;

      this.http
        .post<StudyMaterialResponseDTO>(
          getApiHostUrl() + `/api/v1/study-materials`,
          {
            liveClass: this.selectedLiveClassId,
            name: materialName,
            meetingId,
            sessionDate,
          }
        )
        .pipe(
          catchError(() => {
            this.toastService.showToast('Failed to create new folder', 'error');
            return EMPTY;
          })
        )
        .subscribe((data: StudyMaterialResponseDTO) => {
          this.studyMaterials.push(data);
          this.selectedSession = null;
          cb();
        });
    } else {
      this.toastService.showToast('Invalid folder name', 'error');
    }
  }

  deleteStudyMaterial(studyMaterialId: string) {
    this.http
      .delete(getApiHostUrl() + `/api/v1/study-materials/${studyMaterialId}`)
      .pipe(
        catchError(() => {
          this.toastService.showToast(
            'Removing study material failed!',
            'error'
          );
          return EMPTY;
        })
      )
      .subscribe(() => {
        const itemIndex = this.studyMaterials.findIndex(
          (material) => material._id == studyMaterialId
        );

        if (itemIndex !== -1 && itemIndex !== undefined)
          this.studyMaterials.splice(itemIndex, 1);

        this.toastService.showToast(
          'Study material removed successfully!',
          'success'
        );
      });
  }

  fetchAllStudyMaterials() {
    if (this.selectedLiveClassId) {
      this.liveClassService
        .getAllStudyMaterials(this.selectedLiveClassId)
        .subscribe((res) => {
          this.studyMaterials = res;
        });
    } else {
      console.log('Not Found!');
    }
  }

  // Check if user has valid active subscription
  hasValidSubscription(): boolean {
    if (!this.selectedLiveClass || this.editModeEnabled) {
      return true; // Instructors in edit mode always have access
    }

    const subscription = this.selectedLiveClass.subscriptions;
    return subscription.some((sub) =>
      [
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.COMPLETE,
      ].includes(sub.status)
    );
  }

  get isAuthorized(): boolean {
    return this.editModeEnabled || this.hasValidSubscription();
  }
}
